import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { NewsArticleNumList } from '@/features/actualidad-hostelera/NewsArticleNumList'
import { createNewsArticleSourceChip } from '@/features/actualidad-hostelera/NewsArticleSourceChip'
import type { NewsArticleBodyProps } from '@/features/actualidad-hostelera/articles'
import { toOfficialSalesWhatsAppHref } from '@/lib/officialContact'
import { cn } from '@/lib/utils'
import { article } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import '@/styles/food-cost-fonts.css'

const k = (f: string) => `news.articles.prohibicion-fumar-terrazas-quien-decide-hosteleria-espana.${f}`

const REF = {
    sanidad: {
        href: 'https://www.sanidad.gob.es/gabinete/notasPrensa.do?id=6968&metodo=detalle',
        domain: 'sanidad.gob.es',
        label: 'Sanidad',
    },
    sanidadMuertes: {
        href: 'https://www.sanidad.gob.es/gabinete/notasPrensa.do?id=6412',
        domain: 'sanidad.gob.es',
        label: 'Sanidad',
    },
    lamoncloa: {
        href: 'https://www.lamoncloa.gob.es/consejodeministros/resumenes/paginas/2026/210726-rueda-prensa-ministros.aspx',
        domain: 'lamoncloa.gob.es',
        label: 'La Moncloa',
    },
    elespanol: {
        href: 'https://www.elespanol.com/invertia/empresas/distribucion/20260725/alvarez-almeida-presidente-hosteleros-ley-tabaco-hara-perder-terrazas-identidad-pais/1003744332697_0.amp.html',
        domain: 'elespanol.com',
        label: 'El Español',
    },
    cope: {
        href: 'https://www.cope.es/programas/la-tarde/audios/facil-prohibir-fumar-terrazas-plena-llegada-turistas-fumador-tenga-levantar-mesa-desplazamos-queremos-soluciones-no-conflictos-vecinos-20260722_3408736.html',
        domain: 'cope.es',
        label: 'Cope',
    },
    ecoavant: {
        href: 'https://www.ecoavant.com/salud/hosteleria-rechaza-prohibicion-fumar-en-terrazas-alerta-su-impacto-turistico_17113_102.html',
        domain: 'ecoavant.com',
        label: 'EcoAvant',
    },
    profesionalhoreca: {
        href: 'https://www.profesionalhoreca.com/la-hosteleria-en-espana-en-2025-280-400-establecimientos-transformacion-y-consolidacion/',
        domain: 'profesionalhoreca.com',
        label: 'Profesionalhoreca',
    },
    boe: {
        href: 'https://www.boe.es/buscar/act.php?id=BOE-A-2010-20138',
        domain: 'boe.es',
        label: 'BOE · Ley 42/2010',
    },
    lne: {
        href: 'https://www.lne.es/asturias/2026/07/23/alvarez-almeida-presidente-hosteleria-espana-132732771.html',
        domain: 'lne.es',
        label: 'LNE',
    },
} as const

const SourceChip = createNewsArticleSourceChip(REF, k)

export function ProhibicionFumarTerrazasArticle({ article: meta }: NewsArticleBodyProps) {
    const { t } = useTranslation()

    const contactWhatsapp = toOfficialSalesWhatsAppHref(
        t(
            k('contactWhatsapp'),
            'Hola, he leído el artículo de la prohibición de fumar en terrazas y me gustaría ver cómo puede afectar a mi local.',
        ),
    )

    const weekActions: ReactNode[] = [
        t(k('weekActions.1'), 'No cambies nada en tu terraza todavía. La ley no está en vigor y no tiene fecha.'),
        t(
            k('weekActions.2'),
            'Si perteneces a una asociación territorial de Hostelería de España, pregunta qué enmiendas están preparando para la tramitación en el Congreso.',
        ),
        t(
            k('weekActions.3'),
            'Revisa el estado del proyecto en el Congreso una vez al mes, no cada semana: en esta fase, los cambios van despacio.',
        ),
        t(
            k('weekActions.4'),
            'Si tienes clientela fija fumadora, empieza a pensar (sin anunciarlo) en un plan B: distribución de mesas, rotación, zonas específicas.',
        ),
        t(
            k('weekActions.5'),
            'No pagues ni firmes nada a quien te prometa «adelantarte» a esta ley. Hoy no existe ningún servicio que puedas contratar para eso, porque el texto puede cambiar en el Congreso.',
        ),
    ]

    const sources = [
        {
            label: t(
                k('sources.1'),
                'Nota de prensa oficial, Ministerio de Sanidad: aprobación del proyecto de ley del tabaco',
            ),
            href: REF.sanidad.href,
        },
        {
            label: t(k('sources.2'), 'La Moncloa, resumen del Consejo de Ministros de 21/07/2026'),
            href: REF.lamoncloa.href,
        },
        {
            label: t(k('sources.3'), 'El Español: entrevista a Álvarez Almeida, presidente de Hostelería de España'),
            href: REF.elespanol.href,
        },
        {
            label: t(k('sources.4'), 'Cope: declaraciones de Álvarez Almeida sobre la prohibición en terrazas'),
            href: REF.cope.href,
        },
        {
            label: t(
                k('sources.5'),
                'EcoAvant: Hostelería de España rechaza la prohibición y aporta la encuesta de 40dB',
            ),
            href: REF.ecoavant.href,
        },
        {
            label: t(k('sources.6'), 'Moncloa.com: cobertura de la aprobación de la ley antitabaco 2026'),
            href: 'https://www.moncloa.com/2026/07/22/gobierno-aprueba-ley-antitabaco-2026-3403607/',
        },
        {
            label: t(k('sources.7'), 'Profesionalhoreca: la hostelería en España en 2025, 280.400 establecimientos'),
            href: REF.profesionalhoreca.href,
        },
        { label: t(k('sources.8'), 'BOE: Ley 42/2010, de 30 de diciembre'), href: REF.boe.href },
        {
            label: t(k('sources.9'), 'La Voz de Asturias / LNE: artículo de origen sobre Álvarez Almeida'),
            href: REF.lne.href,
        },
        {
            label: t(
                k('sources.10'),
                'Nota de prensa, Ministerio de Sanidad: Plan 2024-2027 y cerca de 50.000 muertes al año por tabaco',
            ),
            href: REF.sanidadMuertes.href,
        },
    ]

    return (
        <div className={article.page}>
            <article className={article.shell}>
                <ArticleCoverHero
                    category={meta.category}
                    title={meta.title}
                    dek={
                        <>
                            {t(k('dek.beforeDate'), 'El ')}
                            <strong>{t(k('dek.date'), '21 de julio de 2026')}</strong>
                            {t(
                                k('dek.afterDate'),
                                ' el Gobierno envió al Congreso el proyecto que prohíbe fumar en terrazas. Hostelería de España ',
                            )}
                            <strong>{t(k('dek.cannotStop'), 'no puede pararlo')}</strong>
                            {t(k('dek.afterCannot'), ': solo intentar suavizarlo.')}
                        </>
                    }
                    imageSrc={meta.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Terraza de un bar de barrio: café, periódico y cenicero sobre la mesa, silla vacía',
                    )}
                    byline={
                        <ArticleByline
                            authorName={meta.authorName}
                            readingMinutes={meta.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <div className={article.proseIntro}>
                    <p className={article.p}>
                        {t(
                            k('intro.1'),
                            'Tienes un cliente de toda la vida. Se sienta siempre en la misma mesa de la terraza, pide su café, enciende su cigarro y se queda ahí una hora, con el periódico o el móvil. Paga la consumición y, de paso, te deja el cenicero para limpiar.',
                        )}
                    </p>
                    <p className={article.p}>
                        {t(
                            k('intro.2'),
                            'Ese ritual, tan de barrio, tan de aquí, está a un trámite parlamentario de desaparecer.',
                        )}
                    </p>
                    <p className={cn(article.p, article.pGap2xl)}>
                        {t(
                            k('intro.3'),
                            'Y la pregunta que de verdad importa no es si estás a favor o en contra. Es otra: ¿quién decide esto, y qué margen tiene de verdad quien dice defenderte?',
                        )}
                    </p>
                </div>

                <section id='quien-ha-escrito-esta-ley'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.quien'), '¿Quién ha escrito esta ley?')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(k('quien.p1.before'), 'La redactó el Ministerio de Sanidad. El ')}
                            <strong className={article.cite}>
                                {t(k('quien.p1.date'), '21 de julio de 2026')}
                                <SourceChip source='sanidad' />
                            </strong>
                            {t(
                                k('quien.p1.after'),
                                ', el Consejo de Ministros la aprobó en segunda vuelta como proyecto de ley, no como un simple borrador.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            <span className={article.cite}>
                                {t(
                                    k('quien.p2'),
                                    'Eso importa. El proyecto ya está remitido a las Cortes Generales para empezar su tramitación parlamentaria. Es decir: ya no está en la fase en la que se puede reescribir de arriba abajo. Ahora se negocia con enmiendas, artículo por artículo.',
                                )}
                                <SourceChip source='lamoncloa' />
                            </span>
                        </p>
                    </div>
                </section>

                <section id='en-cristiano'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.enCristiano'), 'Qué prohíbe el proyecto y quién lo impulsa')}
                    </h2>
                    <div className={article.tableWrap}>
                        <table className={article.tableFacts}>
                            <tbody>
                                <tr>
                                    <th scope='row'>{t(k('table.prohibido.label'), 'Se prohíbe fumar y vapear en')}</th>
                                    <td>
                                        {t(
                                            k('table.prohibido.value'),
                                            'Terrazas de bares y restaurantes, playas, piscinas colectivas, instalaciones deportivas, parques nacionales y espectáculos al aire libre',
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.zonas.label'), 'Zonas de protección reforzada')}</th>
                                    <td>
                                        {t(
                                            k('table.zonas.value'),
                                            'A menos de 15 metros de los accesos a centros educativos, universidades, sanitarios, culturales y espacios infantiles',
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.vape.label'), 'Vapeadores')}</th>
                                    <td>
                                        {t(
                                            k('table.vape.value'),
                                            'Se equiparan al tabaco: venta solo en tiendas especializadas, prohibidos a menores de 18 años',
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.impulsa.label'), 'Quién lo impulsa')}</th>
                                    <td>
                                        <span className={article.cite}>
                                            {t(k('table.impulsa.value'), 'Ministerio de Sanidad (Mónica García)')}
                                            <SourceChip source='sanidad' />
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.fase.label'), 'Fase actual')}</th>
                                    <td>
                                        {t(k('table.fase.value'), 'Proyecto de ley remitido a las Cortes Generales')}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>{t(k('table.vigor.label'), 'Si se aprueba')}</th>
                                    <td>
                                        {t(
                                            k('table.vigor.value'),
                                            'Entra en vigor a los 20 días de publicarse en el BOE, con 2 meses para adaptar la señalización',
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <section id='casi-nadie'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.casiNadie'), 'Lo que casi nadie está contando bien')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(
                                k('casiNadie.p1.before'),
                                'Fíjate en un matiz que se está perdiendo en la cobertura: parte de la prensa habla de «anteproyecto», pero la nota oficial del Ministerio de Sanidad dice textualmente que el Gobierno aprobó un ',
                            )}
                            <strong className={article.cite}>
                                {t(k('casiNadie.p1.strong'), '«proyecto de ley»')}
                                <SourceChip source='sanidad' />
                            </strong>
                            {t(k('casiNadie.p1.mid'), ' que ')}
                            <strong>
                                {t(
                                    k('casiNadie.p1.cite'),
                                    '«será remitido a las Cortes Generales para iniciar su tramitación parlamentaria»',
                                )}
                            </strong>
                            {t(
                                k('casiNadie.p1.after'),
                                '. No es lo mismo. Un anteproyecto todavía admite cambios de fondo. Un proyecto de ley ya está fijado y se pelea a golpe de enmienda.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('casiNadie.p2'),
                                'Y hay otro dato que interesa más al hostelero que al titular: la fase en la que el sector podía presentar alegaciones sobre el primer borrador (el trámite de audiencia pública) es previa a esta aprobación. Es decir, ya pasó. La ventana en la que Hostelería de España tenía más margen para cambiar el texto de fondo se cerró antes de este verano.',
                            )}
                        </p>
                    </div>
                </section>

                <aside className={article.breakout} aria-label={t(k('breakout.aria'), 'El margen real')}>
                    <div className={article.breakCopy}>
                        <p className={article.breakEyebrow}>{t(k('breakout.eyebrow'), 'El margen real')}</p>
                        <p className={article.breakLead}>
                            {t(
                                k('breakout.lead'),
                                'A partir de ahora, el margen de la patronal es político, no técnico.',
                            )}
                        </p>
                        <p className={article.p}>
                            {t(k('breakout.p'), 'Eso puede torcer artículos concretos. No decide la votación final.')}
                        </p>
                    </div>
                </aside>

                <section id='mala-noticia'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.malaNoticia'), 'La patronal llega al Congreso sin datos de negocio')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(
                                k('mala.p1.before'),
                                'A partir de ahora, el margen de la patronal es político, no técnico. José Luis Álvarez Almeida, presidente de Hostelería de España, ha dicho que ',
                            )}
                            <strong className={article.cite}>
                                {t(k('mala.p1.strong'), 'contactará con los partidos a partir de septiembre de 2026')}
                                <SourceChip source='elespanol' />
                            </strong>
                            {t(k('mala.p1.after'), ' para buscar cambios.')}
                        </p>
                        <p className={article.p}>
                            {t(
                                k('mala.p2.before'),
                                'Y llega a esa negociación sin un argumento que suele pesar mucho en el Congreso: datos económicos propios. Su munición principal es una encuesta de opinión (40dB, 2025) según la cual ',
                            )}
                            <strong className={article.cite}>
                                {t(
                                    k('mala.p2.pct'),
                                    'un 69,3 % de los españoles cree que las campañas informativas son más eficaces que las prohibiciones',
                                )}
                                <SourceChip source='ecoavant' />
                            </strong>
                            {t(k('mala.p2.after'), '. Es un dato de opinión, no de impacto en el negocio.')}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('mala.p3.before'),
                                'Enfrente, el Gobierno maneja una cifra que pesa distinto en un debate de salud pública: ',
                            )}
                            <strong className={article.cite}>
                                {t(k('mala.p3.strong'), 'alrededor de 50.000 muertes al año por tabaquismo en España')}
                                <SourceChip source='sanidadMuertes' />
                            </strong>
                            {t(
                                k('mala.p3.after'),
                                '. Con lo que se sabe hoy, no hay ninguna señal de que el proyecto vaya a retirarse.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='cuenta-de-la-vieja'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.cuentaVieja'), '280.400 bares frente a 176 escaños')}
                    </h2>
                    <div className={article.stats}>
                        <div className={article.stat}>
                            <p className={article.statLabel}>{t(k('cuenta.venues.label'), 'Establecimientos')}</p>
                            <p className={article.statValue}>{t(k('cuenta.venues.value'), '280.400')}</p>
                        </div>
                        <div className={article.stat}>
                            <p className={article.statLabel}>{t(k('cuenta.seats.label'), 'Escaños en el Congreso')}</p>
                            <p className={article.statValue}>{t(k('cuenta.seats.value'), '350')}</p>
                        </div>
                        <div className={article.statAccent}>
                            <p className={article.statLabelAccent}>
                                {t(k('cuenta.majority.label'), 'Mayoría absoluta')}
                            </p>
                            <p className={article.statValueAccent}>{t(k('cuenta.majority.value'), '176')}</p>
                        </div>
                    </div>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(k('cuenta.p1.before'), 'El sector cerró 2025 con ')}
                            <strong className={article.cite}>
                                {t(k('cuenta.p1.strong'), '280.400 establecimientos de hostelería')}
                                <SourceChip source='profesionalhoreca' />
                            </strong>
                            {t(
                                k('cuenta.p1.after'),
                                ' en España. El Congreso de los Diputados tiene 350 escaños, y la mayoría absoluta son 176.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('cuenta.p2'),
                                'Da igual cuántos bares seas si detrás no hay votos parlamentarios repartidos entre grupos. Lo que Hostelería de España tiene de verdad no es un bloque de votos, es capacidad de presión mediática y de ofrecer argumentos a los diputados que ya tengan dudas sobre partes del texto. Eso puede torcer artículos concretos. No decide la votación final.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='ultima-vez'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.ultimaVez'), 'Lo que aprendimos la última vez')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            <span className={article.cite}>
                                {t(
                                    k('ultima.p1'),
                                    'Esta no es la primera vez que se toca la misma norma. La Ley 42/2010, la que prohibió fumar en espacios interiores, modificaba la misma Ley 28/2005 que ahora se retoca. Se publicó el 30 de diciembre de 2010 y entró en vigor el 2 de enero de 2011.',
                                )}
                                <SourceChip source='boe' />
                            </span>
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            <span className={article.cite}>
                                {t(
                                    k('ultima.p2'),
                                    'Aquella ley fue la que, de rebote, convirtió la terraza en el refugio del fumador y disparó su número en toda España. El propio Almeida lo recuerda como argumento: la hostelería no desapareció, se adaptó y, en parte, creció por ese cambio. No tenemos un dato propio verificado de cuánto tardó exactamente aquella tramitación parlamentaria completa, así que no te lo vamos a dar como si lo tuviéramos.',
                                )}
                                <SourceChip source='lne' />
                            </span>
                        </p>
                    </div>
                </section>

                <section id='puede-parar'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(
                            k('h2.puedeParar'),
                            '¿Puede Hostelería de España parar la prohibición de fumar en terrazas?',
                        )}
                    </h2>
                    <blockquote className={article.quoteSm}>
                        {t(k('quote'), 'No, no puede pararla por sí sola. La patronal no vota en el Congreso.')}
                    </blockquote>
                    <div className={article.prose}>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('puede.p1'),
                                'Lo que sí puede hacer, de forma realista: retrasar el calendario, colar excepciones concretas (por ejemplo, para terrazas sin cerramiento, distancias entre mesas o un periodo de adaptación más largo) y mantener el tema en la agenda mediática para presionar a los grupos con más peso electoral en zonas turísticas. Eso es lo que hay. Ni catastrofismo ni victoria.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='esta-semana'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.estaSemana'), 'Qué mirar en tu terraza mientras dura el trámite')}
                    </h2>
                    <div className={article.prose}>
                        <NewsArticleNumList items={weekActions} />
                    </div>
                </section>

                <section id='pendientes'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.pendientes'), 'Anteproyecto o proyecto, fecha de vigor y excepciones')}
                    </h2>
                    <div className={article.prose}>
                        <ul className={article.dashListGap2xl}>
                            <li className={article.dashItem}>
                                {t(
                                    k('pendientes.1'),
                                    'La propia denominación del texto varía según la fuente: unos medios hablan de «anteproyecto», la nota oficial de Sanidad habla de «proyecto de ley» ya remitido a las Cortes. Nos quedamos con la versión oficial, pero lo señalamos porque cambia la fase real en la que estamos.',
                                )}
                            </li>
                            <li className={article.dashItem}>
                                {t(
                                    k('pendientes.2'),
                                    'No hay fecha confirmada de aprobación definitiva ni de entrada en vigor. Dependerá de lo que tarde el Congreso, y eso hoy es una incógnita.',
                                )}
                            </li>
                            <li className={article.dashItem}>
                                {t(
                                    k('pendientes.3'),
                                    'No está claro si la negociación de la patronal con los partidos, prevista para después del verano, logrará excepciones concretas o si el texto llegará prácticamente intacto a la votación final.',
                                )}
                            </li>
                        </ul>
                    </div>
                </section>

                <div className={article.prose}>
                    <p className={article.p}>
                        {t(
                            k('closing.p1'),
                            'Nosotros vamos a seguir este trámite y actualizaremos este artículo según avance por el Congreso.',
                        )}
                    </p>
                    <p className={cn(article.p, article.pGap2xl)}>
                        {t(
                            k('closing.p2'),
                            'Si sale algo nuevo sobre esto, lo cuentas aquí antes que en ningún sitio.',
                        )}
                    </p>
                </div>

                <section className={article.cta}>
                    <h2 className={cn(article.h2, article.h2Cta)}>
                        {t(k('h2.cta'), '¿Esta ley te cambia la terraza y no sabes cómo te pega en caja?')}
                    </h2>
                    <p className={article.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Cuéntanos cómo es tu local y te ayudamos a mirar números de verdad, no titulares.',
                        )}
                    </p>
                    <div className={article.ctaActionsCenter}>
                        <a
                            href={contactWhatsapp}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(article.btn, article.btnSolid)}
                        >
                            {t(k('cta.whatsapp'), 'Escríbenos por WhatsApp')}
                        </a>
                    </div>
                </section>

                <section className={article.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={article.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={article.sourcesList}>
                        {sources.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={article.link}
                                >
                                    {source.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
